import React, { useEffect, useState } from "react";

import {
  FaEdit,
  FaMapMarkerAlt,
  FaPhone,
  FaPlus,
  FaTrash,
  FaUserTie,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import CompactActionBar from "../components/CompactActionBar";
import ConfirmDialog from "../components/ConfirmDialog";
import { PermissionGuard } from "../components/PermissionGuard";
import "../css/pages/Brokers.css";
import {
  Broker,
  brokerService,
  CreateBrokerData,
} from "../services/brokerService";

const Brokers: React.FC = () => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBroker, setEditingBroker] = useState<Broker | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [brokerToDelete, setBrokerToDelete] = useState<Broker | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Form data
  const [formData, setFormData] = useState<CreateBrokerData>({
    name: "",
    mobile: "",
    address: "",
    projectId: 0,
  });

  useEffect(() => {
    fetchBrokers();
  }, [currentPage, searchTerm]);

  const fetchBrokers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit,
        ...(searchTerm && { search: searchTerm }),
      };

      const response = await brokerService.getBrokers(params);
      setBrokers(response.brokers);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error("Error fetching brokers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingBroker) {
        await brokerService.updateBroker(editingBroker.id, formData);
      } else {
        await brokerService.createBroker(formData);
      }

      setShowModal(false);
      setEditingBroker(null);
      resetForm();
      fetchBrokers();
    } catch (error) {
      console.error("Error saving broker:", error);
    }
  };

  const handleEdit = (broker: Broker) => {
    navigate(`/broker/edit?id=${broker.id}`);
  };

  const handleView = (broker: Broker) => {
    navigate(`/broker/view?id=${broker.id}`);
  };

  const handleDelete = (broker: Broker) => {
    setBrokerToDelete(broker);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (brokerToDelete) {
      setDeletingId(brokerToDelete.id);
      try {
        await brokerService.deleteBroker(brokerToDelete.id);
        fetchBrokers();
      } catch (error) {
        console.error("Error deleting broker:", error);
      } finally {
        setDeletingId(null);
      }
    }
    setShowDeleteDialog(false);
    setBrokerToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      mobile: "",
      address: "",
      projectId: 0,
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  if (loading && brokers.length === 0) {
    return <div className="loading">Loading brokers...</div>;
  }

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <input
            type="text"
            placeholder="Search brokers..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{
              padding: "6px 12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "13px",
              minWidth: "300px",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Show:</span>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <PermissionGuard permission="broker.create">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/broker/add')}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Broker
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {loading ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading brokers...</div>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Broker Name</th>
              <th>Mobile</th>
              <th>Project/Site</th>
              <th>Address</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {brokers.map((broker, index) => (
              <tr key={broker.id}>
                <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                  {brokers.length - index}
                </td>
                <td>
                  <div className="broker-name">
                    <FaUserTie className="broker-icon" />
                    {broker.name}
                  </div>
                </td>
                <td>
                  <div className="broker-mobile">
                    <FaPhone className="mobile-icon" />
                    {broker.mobile}
                  </div>
                </td>
                <td>
                  <div style={{ 
                    padding: "4px 8px", 
                    background: "#f0f9ff", 
                    border: "1px solid #bfdbfe",
                    borderRadius: "4px",
                    fontSize: "12px",
                    color: "#1e40af",
                    fontWeight: "500",
                    display: "inline-block"
                  }}>
                    {broker.project?.name || 'N/A'}
                  </div>
                </td>
                <td>
                  <div className="broker-address">
                    {broker.address ? (
                      <>
                        <FaMapMarkerAlt className="address-icon" />
                        {broker.address}
                      </>
                    ) : (
                      <span className="no-address">No address provided</span>
                    )}
                  </div>
                </td>
                <td>
                  <div className="broker-actions">
                    <PermissionGuard permission="broker.edit">
                      <button
                        className="btn-icon edit"
                        onClick={() => handleEdit(broker)}
                        title="Edit Broker"
                      >
                        <FaEdit />
                      </button>
                    </PermissionGuard>
                    <PermissionGuard permission="broker.delete">
                      <button
                        className="btn-icon delete"
                        onClick={() => handleDelete(broker)}
                        title="Delete Broker"
                        disabled={deletingId === broker.id}
                      >
                        {deletingId === broker.id ? (
                          <span className="spinner-small"></span>
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </PermissionGuard>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {brokers.length === 0 && !loading && (
        <div className="empty-state">
          <FaUserTie className="empty-icon" />
          <h3>No Brokers Found</h3>
          <p>Start by adding your first broker</p>
          <PermissionGuard permission="broker.create">
            <button
              className="add-broker-btn"
              onClick={() => setShowModal(true)}
            >
              <FaPlus /> Add Broker
            </button>
          </PermissionGuard>
        </div>
      )}
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const startPage = Math.max(1, currentPage - 2);
              const page = Math.min(startPage + i, totalPages);

              if (page > totalPages) return null;

              return (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            }).filter(Boolean)}
          </div>

          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Broker Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingBroker ? "Edit Broker" : "Add New Broker"}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowModal(false);
                  setEditingBroker(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Broker Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter broker name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile Number *</label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Enter mobile number"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter address (optional)"
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setEditingBroker(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingBroker ? "Update Broker" : "Add Broker"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Broker"
        message={`Are you sure you want to delete "${brokerToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteDialog(false);
          setBrokerToDelete(null);
        }}
      />
    </div>
  );
};

export default Brokers;
